import type { ButtonBaseProps } from '@mui/material/ButtonBase';
import ButtonBase from '@mui/material/ButtonBase';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';

import { varAlpha } from 'minimal-shared/utils';

import { Iconify } from '../../iconify/Iconify';

export type BaseOptionProps = ButtonBaseProps & {
    label: string;
    tooltip?: string;
    selected: boolean;
    icon: React.ReactNode;
    onChangeOption: () => void;
};

const ItemRoot = styled(ButtonBase, {
    shouldForwardProp: (prop: string) => !['selected', 'sx'].includes(prop),
})<{ selected: boolean }>(({ selected, theme }) => ({
    cursor: 'pointer',
    flexDirection: 'column',
    alignItems: 'flex-start',
    padding: theme.spacing(2, 2.5),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    borderRadius: theme.shape.borderRadius * 2,
    border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
    '&:hover': {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
    },
    ...(selected && {
        backgroundColor: varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
    }),
}));

const TopContainer = styled('div')(({ theme }) => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    justifyContent: 'space-between',
}));

const BottomContainer = styled('div')(() => ({
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const ItemLabel = styled('span')(({ theme }) => ({
    lineHeight: '18px',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: theme.typography.fontWeightSemiBold,
}));

/**
 * BaseOption Component
 *
 * A reusable option component for settings drawers that displays an icon, label, tooltip, and a toggle switch.
 *
 * @param {object} props - The component props
 * @param {SxProps} [props.sx] - The system styling props for the component
 * @param {React.ReactNode} props.icon - The icon to display in the top container
 * @param {string} props.label - The label text to display
 * @param {string} [props.tooltip] - Optional tooltip text that appears when hovering over the info icon
 * @param {boolean} props.selected - Whether the option is selected/active
 * @param {() => void} props.onChangeOption - Callback function for when the option is clicked
 * @param {object} props.other - Any additional props to pass to the root element
 *
 * @returns {JSX.Element} The rendered BaseOption component
 */
export function BaseOption({
    sx,
    icon,
    label,
    tooltip,
    selected,
    onChangeOption,
    ...other
}: BaseOptionProps) {
    return (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <ItemRoot disableRipple selected={selected} onClick={onChangeOption} sx={sx} {...other}>
            <TopContainer>
                {icon}
                <Switch
                    name={label}
                    size="small"
                    color="default"
                    checked={selected}
                    sx={{ mr: -0.75 }}
                />
            </TopContainer>

            <BottomContainer>
                <ItemLabel>{label}</ItemLabel>

                {tooltip && (
                    <Tooltip
                        arrow
                        title={tooltip}
                        slotProps={{ tooltip: { sx: { maxWidth: 240, mr: 0.5 } } }}
                    >
                        <Iconify
                            width={16}
                            icon="eva:info-outline"
                            sx={{ cursor: 'pointer', color: 'text.disabled' }}
                        />
                    </Tooltip>
                )}
            </BottomContainer>
        </ItemRoot>
    );
}
